import { useEffect, useMemo, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@lib/context/AuthContextType';
import { getPortalSchedule, getTimeEntries, getPortalJobs, getMyPay, type ScheduleEvent, type TimeEntry, type PortalJob, type MyPay } from '@lib/api/portal';
import apiClient from '@lib/api/client';
import { LargeTitleHeader, PullToRefresh } from '@/components/common';
import { DocumentViewerModal } from '@/components/common/DocumentViewerModal';
import { computeHoursStats } from '../../lib/hoursStats';
import { ScheduleSection } from './EmployeeSchedulePage';
import { getForecast, weatherIcon, type DayForecast } from '@/lib/api/weather';

interface FlaggedDoc {
  flag_id: number; note?: string | null; flagged_at: string;
  doc_id: number; name: string; original_name?: string; type: string; url: string;
  job_id?: number | null; job_title?: string | null;
}

// Today's checkable tasks (CR #104) — same feed the timesheet uses
interface DayTask {
  id: number; title: string; description?: string | null; status: string;
  approval_status?: string; job_title?: string | null;
  start_time?: string | null; end_time?: string | null;
  checklist?: { id: number; text: string; checked: number | boolean }[];
}

interface SharedList {
  id: number; name: string; category?: string | null; status: 'open' | 'claimed' | 'fulfilled';
  items: { label: string; value?: string }[];
  created_by_name?: string | null; assigned_to_name?: string | null; created_at: string;
}

// Local YYYY-MM-DD
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const day = (s?: string | null) => (s || '').slice(0, 10);

const openMaps = (address: string) =>
  window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');

// Analytics-style home: what's happening + my numbers. Time LOGGING lives on the
// Timesheet tab only — nothing here writes hours.
export const EmployeeHomePage = () => {
  const { user } = useContext(AuthContext)!;
  const [schedule, setSchedule] = useState<ScheduleEvent[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [jobs, setJobs] = useState<PortalJob[]>([]);
  const [myPay, setMyPay] = useState<MyPay | null>(null);
  const [flagged, setFlagged] = useState<FlaggedDoc[]>([]);
  // CR #131 — crew board memos + safety talks needing sign-off
  const [memos, setMemos] = useState<{ id: number; title: string; body: string; category: string; pinned: number; requires_ack: number; acked_at: string | null; created_by_name?: string }[]>([]);
  const [talks, setTalks] = useState<{ id: number; title: string; talk_date: string; required: number; acked_at: string | null }[]>([]);
  const loadBoard = () => {
    apiClient.get('/crew-memos/me/list').then(r => setMemos(r.data.data ?? [])).catch(() => {});
    apiClient.get('/safety/me/toolbox-talks').then(r => setTalks(r.data.data ?? [])).catch(() => {});
  };
  const ackMemo = async (id: number) => {
    setMemos(ms => ms.map(m => m.id === id ? { ...m, acked_at: new Date().toISOString() } : m));
    try { await apiClient.post(`/crew-memos/me/${id}/ack`); } catch { loadBoard(); }
  };
  const [flagViewer, setFlagViewer] = useState<FlaggedDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  // #104 — tasks with checklists, shared lists, site weather
  const [dayTasks, setDayTasks] = useState<DayTask[]>([]);
  const [openChecklists, setOpenChecklists] = useState<Set<number>>(new Set());
  const [sharedLists, setSharedLists] = useState<SharedList[]>([]);
  const [openLists, setOpenLists] = useState<Set<number>>(new Set());
  const [forecast, setForecast] = useState<DayForecast[]>([]);
  const today = ymd(new Date());

  const reload = () => {
    getPortalSchedule().then(setSchedule).catch(() => {});
    getPortalJobs().then(setJobs).catch(() => {});
    getMyPay().then(setMyPay).catch(() => {});
    apiClient.get('/portal/me/flagged-docs').then(r => setFlagged(r.data.data ?? [])).catch(() => {});
    apiClient.get(`/portal/me/day-tasks?date=${ymd(new Date())}`).then(r => setDayTasks(r.data.data ?? [])).catch(() => {});
    apiClient.get('/portal/me/shared-lists').then(r => setSharedLists(r.data.data ?? [])).catch(() => {});
    loadBoard();
    getForecast().then(setForecast).catch(() => {});
    getTimeEntries().then(setEntries).catch(() => setError('Failed to load data. Please refresh.'));
  };
  useEffect(() => { reload(); }, []);

  // Task + checklist toggles mirror the timesheet page's behaviour
  const toggleTask = async (t: DayTask) => {
    if (t.approval_status === 'pending') return;
    const done = t.status === 'completed';
    setDayTasks(prev => prev.map(x => x.id === t.id ? { ...x, status: done ? 'pending' : 'completed' } : x));
    try { await apiClient.post(`/portal/me/tasks/${t.id}/complete`, { completed: !done }); }
    catch { setDayTasks(prev => prev.map(x => x.id === t.id ? { ...x, status: t.status } : x)); }
  };

  const toggleCheckItem = async (taskId: number, ci: { id: number; checked: number | boolean }) => {
    const next = !ci.checked;
    setDayTasks(prev => prev.map(t => t.id === taskId
      ? { ...t, checklist: (t.checklist || []).map(c => c.id === ci.id ? { ...c, checked: next } : c) }
      : t));
    try { await apiClient.post(`/portal/me/checklist/${ci.id}`, { checked: next }); }
    catch {
      setDayTasks(prev => prev.map(t => t.id === taskId
        ? { ...t, checklist: (t.checklist || []).map(c => c.id === ci.id ? { ...c, checked: !next } : c) }
        : t));
    }
  };

  const setListStatus = async (l: SharedList, status: 'claimed' | 'fulfilled') => {
    try {
      await apiClient.post(`/portal/me/shared-lists/${l.id}/status`, { status });
      setSharedLists(prev => prev.map(x => x.id === l.id ? { ...x, status, assigned_to_name: status === 'claimed' ? (user?.username || 'you') : x.assigned_to_name } : x));
    } catch { /* refresh will restore truth */ }
  };

  const counted = useMemo(() => entries.filter(e => e.status !== 'rejected'), [entries]);

  // Week window helper (Mon–Sun) offset by n weeks back from today
  const weekWindow = (weeksBack: number) => {
    const d = new Date();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((d.getDay() + 6) % 7) - weeksBack * 7);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: ymd(mon), to: ymd(sun) };
  };
  const sumRange = (from: string, to: string) =>
    counted.filter(e => day(e.work_date) >= from && day(e.work_date) <= to)
      .reduce((s, e) => s + Number(e.hours), 0);

  const thisWeek = (() => { const w = weekWindow(0); return sumRange(w.from, w.to); })();
  const monthStart = today.slice(0, 8) + '01';
  const thisMonth = sumRange(monthStart, today);
  const daysWorkedMonth = new Set(counted.filter(e => day(e.work_date) >= monthStart && day(e.work_date) <= today).map(e => day(e.work_date))).size;
  const pendingHours = entries.filter(e => e.status === 'submitted').reduce((s, e) => s + Number(e.hours), 0);
  const activeJobs = jobs.filter(j => !['completed', 'cancelled'].includes((j.status || '').toLowerCase()));

  // Last 6 weeks of hours — single-series bar chart (brand hue)
  const weekly = useMemo(() => {
    const out: { label: string; hours: number }[] = [];
    for (let wb = 5; wb >= 0; wb--) {
      const w = weekWindow(wb);
      out.push({
        label: wb === 0 ? 'This wk' : new Date(w.from + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
        hours: sumRange(w.from, w.to),
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counted]);
  const maxWeekly = Math.max(...weekly.map(w => w.hours), 1);

  const todayJobs = schedule.filter(s => day(s.scheduled_date) <= today && (day(s.end_date) || day(s.scheduled_date)) >= today);

  const markRead = async (f: FlaggedDoc) => {
    setFlagged(prev => prev.filter(x => x.flag_id !== f.flag_id));
    try { await apiClient.post(`/portal/me/flagged-docs/${f.flag_id}/read`, {}); }
    catch { setFlagged(prev => [f, ...prev]); }
  };

  const tile = (icon: string, label: string, value: string, sub?: string) => (
    <div className="bg-white rounded-xl border border-gray-200 p-3.5">
      <div className="flex items-center gap-1.5 text-gray-500">
        <span className="material-symbols-outlined text-base text-[#C77D3F]">{icon}</span>
        <span className="text-xs font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-1 leading-none">{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <PullToRefresh onRefresh={reload}>
      <div className="space-y-4 pb-4">
        <LargeTitleHeader
          title={`Hey, ${user?.username || 'there'}`}
          subtitle={new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
          actions={
            <div className="flex items-center gap-1.5">
              <Link to="/portal/employee/bc-code" title="BC Building Code 2024"
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-[#C77D3F]/40 bg-[#C77D3F]/10 text-[#C77D3F] active:bg-[#C77D3F]/20">
                <span className="material-symbols-outlined text-xl">menu_book</span>
              </Link>
              <Link to="/portal/employee/calculator" title="Calculator"
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-500 active:bg-gray-100">
                <span className="material-symbols-outlined text-xl">calculate</span>
              </Link>
              <Link to="/portal/employee/help" title="Help"
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-500 active:bg-gray-100">
                <span className="material-symbols-outlined text-xl">help</span>
              </Link>
            </div>
          }
        />
        {error && <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700"><span className="material-symbols-outlined text-base">error</span>{error}</div>}

        {/* CR #131 — crew board + safety sign-offs, right at the top */}
        {(memos.length > 0 || talks.some(t => t.required !== 0 && !t.acked_at)) && (
          <div className="space-y-2">
            {talks.filter(t => t.required !== 0 && !t.acked_at).slice(0, 3).map(t => (
              <Link key={`talk-${t.id}`} to="/portal/employee/safety"
                className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl active:bg-amber-100">
                <span className="material-symbols-outlined text-amber-600">health_and_safety</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-amber-900 truncate">{t.title}</span>
                  <span className="block text-xs text-amber-700">Safety talk — tap to read and sign off</span>
                </span>
                <span className="material-symbols-outlined text-amber-400">chevron_right</span>
              </Link>
            ))}
            {memos.slice(0, 4).map(m => {
              const urgent = m.category === 'urgent';
              return (
                <div key={`memo-${m.id}`}
                  className={`px-4 py-3 rounded-xl border ${urgent ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-start gap-2.5">
                    <span className={`material-symbols-outlined ${urgent ? 'text-red-600' : 'text-[#C77D3F]'}`}>
                      {urgent ? 'priority_high' : m.category === 'kudos' ? 'celebration' : m.category === 'schedule' ? 'event' : 'campaign'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${urgent ? 'text-red-900' : 'text-gray-900'}`}>
                        {!!m.pinned && '📌 '}{m.title}
                      </p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{m.body}</p>
                      {m.created_by_name && <p className="text-[11px] text-gray-400 mt-1">— {m.created_by_name}</p>}
                      {!!m.requires_ack && (
                        m.acked_at
                          ? <p className="text-xs text-green-700 font-semibold mt-1.5">✓ You confirmed this</p>
                          : <button onClick={() => ackMemo(m.id)}
                              className="mt-2 h-9 px-4 rounded-lg bg-[#C77D3F] text-white text-sm font-bold active:scale-95">
                              Got it
                            </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Flagged docs — pinned until marked read */}
        {flagged.length > 0 && (
          <div className="space-y-2">
            {flagged.map(f => (
              <div key={f.flag_id} className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3.5">
                <div className="flex items-start gap-2.5">
                  <span className="material-symbols-outlined text-amber-500 text-xl mt-0.5">flag</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide">Review required</p>
                    <p className="font-semibold text-gray-900 text-sm truncate">{f.name}</p>
                    {f.job_title && <p className="text-xs text-gray-500 truncate">{f.job_title}</p>}
                    {f.note && <p className="text-xs text-amber-800 mt-1 bg-amber-100/60 rounded px-2 py-1">{f.note}</p>}
                  </div>
                </div>
                <div className="flex gap-2 mt-2.5">
                  <button onClick={() => setFlagViewer(f)}
                    className="flex-1 h-9 rounded-lg bg-[#C77D3F] text-white text-xs font-bold flex items-center justify-center gap-1 active:scale-[0.98]">
                    <span className="material-symbols-outlined text-base">visibility</span> Open document
                  </button>
                  <button onClick={() => markRead(f)}
                    className="flex-1 h-9 rounded-lg border border-amber-300 bg-white text-amber-700 text-xs font-bold flex items-center justify-center gap-1 active:bg-amber-50">
                    <span className="material-symbols-outlined text-base">check_circle</span> Mark as read
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Site weather (#104) — 7 days, risk-graded like the office dashboard */}
        {forecast.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-3.5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[#C77D3F] text-xl">partly_cloudy_day</span>
                Site weather
              </h2>
              <span className="text-[10px] text-gray-400">Duncan / Cowichan</span>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {forecast.slice(0, 7).map((d, i) => (
                <div key={d.date}
                  className={`flex flex-col items-center gap-0.5 rounded-lg py-1.5 ${d.risk === 'high' ? 'bg-red-50' : d.risk === 'caution' ? 'bg-amber-50' : ''}`}>
                  <span className="text-[10px] font-semibold text-gray-500">
                    {i === 0 ? 'Today' : new Date(`${d.date}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short' })}
                  </span>
                  <span className={`material-symbols-outlined text-lg leading-none ${d.risk === 'high' ? 'text-red-600' : d.risk === 'caution' ? 'text-amber-600' : 'text-gray-700'}`}>
                    {weatherIcon(d.code)}
                  </span>
                  <span className="text-[11px] font-bold text-gray-800">{d.t_max}°</span>
                  {d.precip_prob >= 40 && <span className="text-[9px] font-semibold text-blue-500">{d.precip_prob}%</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Schedule first — calendar, day tasks, timeline lead the page */}
        <ScheduleSection />

        {/* Today's tasks with sub-task checklists (#104) — checkable right here */}
        {dayTasks.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2 text-sm">
              <span className="material-symbols-outlined text-[#C77D3F] text-xl">checklist</span>
              Today's tasks
              <span className="ml-auto text-[11px] font-semibold text-gray-400">
                {dayTasks.filter(t => t.status === 'completed').length}/{dayTasks.length} done
              </span>
            </h2>
            <p className="text-[11px] text-gray-400 mb-2.5">Check off what you finish — it updates the office board live.</p>
            <div className="space-y-1.5">
              {dayTasks.map(t => (
                <div key={t.id} className="border border-gray-100 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={t.status === 'completed'}
                      disabled={t.approval_status === 'pending'}
                      onChange={() => toggleTask(t)}
                      className="h-4.5 w-4.5 accent-[#C77D3F]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${t.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-800 font-medium'}`}>
                        {t.title}
                        {t.approval_status === 'pending' && <span className="ml-1.5 text-[10px] text-amber-600 font-semibold">awaiting approval</span>}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate">
                        {[t.job_title, t.start_time && String(t.start_time).slice(0, 5)].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    {(t.checklist?.length ?? 0) > 0 && (
                      <button
                        onClick={() => setOpenChecklists(prev => {
                          const n = new Set(prev);
                          if (n.has(t.id)) n.delete(t.id); else n.add(t.id);
                          return n;
                        })}
                        className="text-[10px] font-bold text-[#8a5526] bg-[#C77D3F]/10 rounded-full px-2 py-0.5 flex items-center gap-0.5"
                      >
                        {(t.checklist || []).filter(c => c.checked).length}/{t.checklist!.length}
                        <span className={`material-symbols-outlined text-[12px] leading-none transition-transform ${openChecklists.has(t.id) ? 'rotate-180' : ''}`}>expand_more</span>
                      </button>
                    )}
                  </div>
                  {openChecklists.has(t.id) && (t.checklist?.length ?? 0) > 0 && (
                    <div className="mt-1.5 ml-7 space-y-1">
                      {t.checklist!.map(ci => (
                        <label key={ci.id} className="flex items-center gap-2 text-xs">
                          <input type="checkbox" checked={!!ci.checked}
                            onChange={() => toggleCheckItem(t.id, ci)}
                            className="h-3.5 w-3.5 accent-[#C77D3F]" />
                          <span className={ci.checked ? 'text-gray-400 line-through' : 'text-gray-700'}>{ci.text}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shared lists (#104) — crew orders/material lists, claim & fulfil */}
        {sharedLists.filter(l => l.status !== 'fulfilled').length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-900 mb-2.5 flex items-center gap-2 text-sm">
              <span className="material-symbols-outlined text-[#C77D3F] text-xl">list_alt</span>
              Shared lists
              <Link to="/portal/employee/calculator" className="ml-auto text-[11px] font-semibold text-[#C77D3F]">New list →</Link>
            </h2>
            <div className="space-y-2">
              {sharedLists.filter(l => l.status !== 'fulfilled').slice(0, 5).map(l => (
                <div key={l.id} className="border border-gray-100 rounded-lg px-3 py-2">
                  <button className="w-full flex items-center gap-2 text-left"
                    onClick={() => setOpenLists(prev => {
                      const n = new Set(prev);
                      if (n.has(l.id)) n.delete(l.id); else n.add(l.id);
                      return n;
                    })}>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 truncate">{l.name}</span>
                      <span className="block text-[11px] text-gray-400 truncate">
                        {l.items.length} item{l.items.length === 1 ? '' : 's'}
                        {l.created_by_name ? ` · from ${l.created_by_name}` : ''}
                        {l.status === 'claimed' && l.assigned_to_name ? ` · claimed by ${l.assigned_to_name}` : ''}
                      </span>
                    </span>
                    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${l.status === 'open' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                      {l.status}
                    </span>
                    <span className={`material-symbols-outlined text-base text-gray-300 transition-transform ${openLists.has(l.id) ? 'rotate-180' : ''}`}>expand_more</span>
                  </button>
                  {openLists.has(l.id) && (
                    <div className="mt-1.5 space-y-1">
                      {l.items.map((it, i) => (
                        <p key={i} className="text-xs text-gray-600 flex justify-between gap-2">
                          <span>{it.label}</span>
                          {it.value && <span className="text-gray-400">{it.value}</span>}
                        </p>
                      ))}
                      <div className="flex gap-2 pt-1">
                        {l.status === 'open' && (
                          <button onClick={() => setListStatus(l, 'claimed')}
                            className="flex-1 h-8 rounded-lg bg-[#C77D3F] text-white text-xs font-bold">I'll get this</button>
                        )}
                        <button onClick={() => setListStatus(l, 'fulfilled')}
                          className="flex-1 h-8 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold">Mark fulfilled</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-2.5">
          {tile('schedule', 'This week', `${thisWeek.toFixed(1)}h`, 'hours worked')}
          {tile('calendar_month', 'This month', `${thisMonth.toFixed(1)}h`, `${daysWorkedMonth} day${daysWorkedMonth !== 1 ? 's' : ''} worked`)}
          <Link to="/portal/employee/jobs">{tile('construction', 'My jobs', String(activeJobs.length), 'tap to view details')}</Link>
          {tile('hourglass_top', 'Awaiting approval', `${pendingHours.toFixed(1)}h`, 'submitted hours')}
        </div>

        {/* More hours detail */}
        {(() => {
          const h = computeHoursStats(entries);
          const delta = h.thisWeek - h.lastWeek;
          return (
            <div className="grid grid-cols-2 gap-2.5">
              {tile('today', 'Today', `${h.today.toFixed(1)}h`, 'logged so far')}
              {tile('history', 'Last week', `${h.lastWeek.toFixed(1)}h`, delta === 0 ? 'same as this week' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}h this week`)}
              {tile('avg_pace', 'Avg / workday', `${h.avgPerWorkdayMonth.toFixed(1)}h`, 'this month')}
              {tile('functions', 'All time', `${h.allTime.toFixed(1)}h`, `${h.daysWorkedAllTime} days worked`)}
            </div>
          );
        })()}

        {/* Owed banner — earned minus what's been paid out */}
        {myPay?.summary && myPay.summary.owed > 0.005 && (
          <div className="bg-[#C77D3F]/10 border border-[#C77D3F]/30 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-[#7a4a22] font-medium">Owed to you</span>
            <span className="text-lg font-bold text-[#C77D3F]">${myPay.summary.owed.toFixed(2)}</span>
          </div>
        )}

        {/* Weekly hours chart — single series, direct label on peak + current */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 text-sm mb-3">Hours by week</h2>
          <div className="flex items-end gap-2 h-28">
            {weekly.map((w, i) => {
              const isPeak = w.hours === maxWeekly && w.hours > 0;
              const isCurrent = i === weekly.length - 1;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${w.label}: ${w.hours.toFixed(1)}h`}>
                  {(isPeak || (isCurrent && w.hours > 0)) && (
                    <span className="text-[10px] font-semibold text-gray-700 leading-none">{w.hours.toFixed(0)}h</span>
                  )}
                  <div
                    className={`w-full rounded-t ${isCurrent ? 'bg-[#C77D3F]' : 'bg-[#C77D3F]/45'}`}
                    style={{ height: `${Math.max((w.hours / maxWeekly) * 88, w.hours > 0 ? 4 : 2)}px` }}
                  />
                  <span className="text-[10px] text-gray-400 truncate w-full text-center">{w.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Today */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">today</span>
            Today
          </h2>
          {todayJobs.length === 0 ? (
            <p className="text-sm text-gray-500">No jobs scheduled for today.</p>
          ) : (
            <div className="space-y-3">
              {todayJobs.map(job => (
                <div key={job.id} className="text-sm border-l-2 border-[#C77D3F] pl-3">
                  <p className="font-medium text-gray-900">{job.job_title}</p>
                  {job.site_address && (
                    <button type="button" onClick={() => openMaps(job.site_address!)}
                      className="flex items-center gap-1 text-[#C77D3F] text-xs mt-0.5 active:opacity-70">
                      <span className="material-symbols-outlined text-xs">location_on</span>
                      {job.site_address}
                    </button>
                  )}
                  {job.start_time && <p className="text-gray-500 mt-0.5 text-xs">{String(job.start_time).slice(0, 5)}{job.end_time ? ` – ${String(job.end_time).slice(0, 5)}` : ''}</p>}
                  {/* Today's tasks for this event (mirrors the office calendar) */}
                  {job.subtasks && job.subtasks.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {job.subtasks.slice(0, 4).map(st => (
                        <p key={st.id} className={`text-xs flex items-center gap-1 ${st.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                          <span className="material-symbols-outlined text-xs">{st.status === 'completed' ? 'check_circle' : 'chevron_right'}</span>
                          {st.title}
                          {st.start_time && <span className="text-gray-400">({String(st.start_time).slice(0, 5)})</span>}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
      <DocumentViewerModal
        doc={flagViewer ? { id: flagViewer.doc_id, name: flagViewer.name, type: flagViewer.type || '', url: flagViewer.url } : null}
        onClose={() => setFlagViewer(null)}
        onDownload={d => { const a = document.createElement('a'); a.href = d.url; a.download = d.name; a.click(); }}
      />
    </PullToRefresh>
  );
};
